/**
 * useCopyFeedback Hook
 *
 * Copies text to the clipboard and flips a short-lived `copied` flag so callers
 * can show a confirmation (e.g. a green check) that resets on its own. Returns a
 * boolean so the caller decides how to surface failures.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../utils/logger';

export function useCopyFeedback(resetMs = 2000) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), resetMs);
        return true;
      } catch (error) {
        logger.error('Clipboard copy failed', { error });
        return false;
      }
    },
    [resetMs],
  );

  return { copied, copy };
}
